// Dispatch Brief v2, Phase 2 (2026-08-05) — additive-only debug
// instrumentation for the iOS Auth0 handoff-delay investigation. No auth
// behaviour changes; every call site using this module is a log-and-continue
// or log-and-rethrow, never a log-and-alter.
//
// Gated on debug=1 (query param, persisted to localStorage) rather than
// NODE_ENV, because this needs to run in the deployed preview channel build
// (a real production build) for Connor to actually capture a trace on his
// iPad — a dev-only gate would make the whole exercise pointless.
//
// localStorage, not sessionStorage, for two independent reasons:
//   1. Safari strips the query string across the Auth0 redirect round-trip,
//      so the flag has to survive that navigation regardless of storage type
//      — either would do that part.
//   2. The leading hypothesis under test is a service-worker 'controlling'
//      event firing mid-flight and calling window.location.reload(). A
//      same-tab reload preserves sessionStorage, but a FULL app relaunch
//      (closing and reopening the installed iPad app — which is exactly
//      the boundary the boot-counter below exists to distinguish) does not.
//      localStorage is what survives that boundary.
import { FUNCTIONS_ORIGIN } from './functionsOrigin';

const DEBUG_KEY = 'rosteremDebug';
const LOG_KEY = 'rosteremAuthTraceLog';
const BOOT_COUNT_KEY = 'rosteremAuthTraceBootCount';
const MAX_LOG_ENTRIES = 500;

// Orgless-user white-screen investigation (2026-08-08) — the guard-loop
// under investigation can tear the page down before localStorage is ever
// read back off the device (WebKit "A problem repeatedly occurred"), so
// every trace() entry is ALSO fired at this dev-only Cloud Function via
// navigator.sendBeacon, landing in Cloud Functions logs instead. Points at
// whatever project this build's VUE_APP_FIREBASE_PROJECT_ID resolves to —
// harmless against prod: functions/index.js's debugTrace hard-404s on any
// project other than rosterem-dev, so a mis-tagged non-prod-host build
// still carrying prod env values (see the build:dev investigation earlier
// this engagement) just gets a 404 back, not a working exfiltration path.
const DEBUG_TRACE_URL = `${FUNCTIONS_ORIGIN}/debugTrace`;

// Merge-blocker fix (2026-08-05) — exact-match production hostnames. Every
// other host (localhost, *.web.app preview/dev channels, rosterem-dev.*)
// stays eligible for debug=1 — deliberately NOT a blanket "*.web.app" block,
// since rosterem-a0c99.web.app (prod) and preview channels like
// rosterem-dev--debug-xxxx.web.app (not prod) share that suffix.
const PRODUCTION_HOSTS = [
  'rosterem.com.au',
  'rosterem-a0c99.web.app',
  'rosterem-a0c99.firebaseapp.com',
];

// `host` param defaults to the real location.hostname; exported and
// parameterised so tests can exercise every branch directly instead of
// mocking global `location`.
export function isProductionHost(host = (typeof location !== 'undefined' ? location.hostname : '')) {
  // Dev preview-channel deploys (npm run build:dev) resolve behind
  // dev.rosterem.com.au — must be checked BEFORE the '.rosterem.com.au'
  // suffix match below, or it would be wrongly treated as production
  // just for sharing the apex domain with real prod subdomains like
  // service.rosterem.com.au.
  if (host === 'dev.rosterem.com.au') return false;
  if (typeof host !== 'string' || !host) return false;
  return PRODUCTION_HOSTS.includes(host) || host.endsWith('.rosterem.com.au');
}

function computeDebugEnabled() {
  try {
    if (isProductionHost()) {
      // Self-heal: a device that picked up debug=1 (or the persisted flag)
      // before this gate existed must not keep tracing silently on a
      // production host indefinitely.
      localStorage.removeItem(DEBUG_KEY);
      return false;
    }
    // 2026-08-06: an installed PWA launches from the manifest start_url
    // into a fresh storage partition on standalone iOS — no query string
    // (start_url doesn't carry ?debug=1) and no access to any previously
    // persisted localStorage flag (different partition). Query param /
    // persisted-flag gating can never reach that surface, so every
    // non-production host is unconditionally on instead.
    return true;
  } catch (e) {
    return false;
  }
}

// Merge-blocker fix (2026-08-05) — the OAuth `code`/`state` query values are
// single-use/session-scoped credentials and must never reach the persisted
// trace log or console, even though the trace exists specifically to prove
// WHEN a code= landing happened. Keeps the param keys (so "was a code
// present" stays visible) but strips the values.
export function redactUrl(url) {
  if (typeof url !== 'string') return url;
  return url
    .replace(/([?&]code=)[^&]*/gi, '$1<redacted>')
    .replace(/([?&]state=)[^&]*/gi, '$1<redacted>');
}

// Computed once at module load (main.js imports this first) — matches the
// existing sessionRestoreAttempted-style "decide once" pattern already used
// in auth0.js, and avoids re-reading storage on every trace() call.
export const debugEnabled = computeDebugEnabled();

// Increments on every module load, i.e. every real app boot (cold start,
// SW-triggered reload, or a full relaunch) — the brief's "persisted
// boot-counter" ask, and the signal that distinguishes "this reload is the
// SW claiming a genuinely fresh registration" from "this is boot #14 of the
// same install."
export function traceBootCount() {
  try {
    const n = (parseInt(localStorage.getItem(BOOT_COUNT_KEY), 10) || 0) + 1;
    localStorage.setItem(BOOT_COUNT_KEY, String(n));
    return n;
  } catch (e) {
    return -1;
  }
}

export function trace(tag, data) {
  if (!debugEnabled) return;
  const entry = {
    iso: new Date().toISOString(),
    perf: Math.round(performance.now()),
    tag,
    data: data || {},
  };
  // eslint-disable-next-line no-console
  console.log('[auth-trace]', entry.iso, `+${entry.perf}ms`, tag, entry.data);
  try {
    // Fire-and-forget — sendBeacon queues the request and survives the page
    // being torn down immediately after, unlike a normal fetch(). Never
    // awaited, never allowed to throw past this try/catch: local console +
    // localStorage logging above (and below) must never depend on network
    // reachability.
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      // text/plain, not application/json — application/json is a CORS
      // "non-simple" content type and forces a preflight OPTIONS request,
      // which sendBeacon has no mechanism to wait for or retry (it fires
      // once, fire-and-forget). text/plain is CORS-simple (no preflight);
      // the receiving function (functions/index.js's debugTrace) reads the
      // raw body and JSON.parses it itself.
      const blob = new Blob([JSON.stringify(entry)], { type: 'text/plain' });
      navigator.sendBeacon(DEBUG_TRACE_URL, blob);
    }
  } catch (e) {
    // Beacon best-effort — the console line above already fired.
  }
  try {
    // Synchronous write, deliberately not batched/debounced — the
    // sw-controlling-reload-imminent breadcrumb (registerServiceWorker.js)
    // has to land in storage before window.location.reload() tears the page
    // down, or this whole mechanism fails to catch the one event it exists
    // to catch.
    const raw = localStorage.getItem(LOG_KEY);
    const log = raw ? JSON.parse(raw) : [];
    log.push(entry);
    if (log.length > MAX_LOG_ENTRIES) log.splice(0, log.length - MAX_LOG_ENTRIES);
    localStorage.setItem(LOG_KEY, JSON.stringify(log));
  } catch (e) {
    // Storage full/unavailable — the console line above already fired.
  }
}

// eruda's console is where Connor reads this on-device. window.* on purpose:
// a global console helper, not app plumbing.
if (typeof window !== 'undefined') {
  window.rosteremAuthTrace = () => {
    try {
      return JSON.parse(localStorage.getItem(LOG_KEY) || '[]');
    } catch (e) {
      return [];
    }
  };
  window.rosteremAuthTraceClear = () => {
    localStorage.removeItem(LOG_KEY);
    localStorage.removeItem(BOOT_COUNT_KEY);
  };
}
